import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!req.url.startsWith('/api/')) {
    return next(req);
  }

  if (auth.mode() === 'b2c') {
    return sendWithBearer(req, next, auth, false);
  }

  const prepared = applyApiKey(req, auth.apiKey(), auth.email());
  return next(prepared).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401) {
        auth.logout();
        router.navigate(['/login']);
      }
      return throwError(() => err);
    }),
  );
};

function sendWithBearer(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: AuthService,
  isRetry: boolean,
): Observable<HttpEvent<unknown>> {
  return from(auth.bearerToken(isRetry)).pipe(
    switchMap(token =>
      next(applyBearer(req, token)).pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status === 401 && !isRetry) {
            // Token may be stale server-side; force a refresh and retry once.
            return sendWithBearer(req, next, auth, true);
          }
          return throwError(() => err);
        }),
      ),
    ),
  );
}

function applyApiKey(
  req: HttpRequest<unknown>,
  apiKey: string | null,
  email: string | null,
): HttpRequest<unknown> {
  if (!apiKey || !email) return req;
  return req.clone({
    setHeaders: {
      Authorization: `ApiKey ${apiKey}`,
      'X-User-Email': email,
    },
  });
}

function applyBearer(
  req: HttpRequest<unknown>,
  token: string | null,
): HttpRequest<unknown> {
  if (!token) return req;
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}
